import React from 'react';
import { connect } from 'react-redux';
import { compose, pure } from 'recompose';
import moment from 'moment';
import { analytics, analyticsActions, Track } from 'angieslist-app-platform';
import { Button, Col, Input, Panel, Row, Well } from 'angieslist-react-components';
import { stylesColors } from 'angieslist-styles';
import { get, isUndefined, join } from 'lodash';
import { ThimbleTransitionBanner } from 'angieslist-app-platform/lib/browser';
import { defaultPageProps } from '../../../../app/browser/analyticsDefaultProperties';
import { itemClicked } from '../../../../app/browser/analyticsHelpers';
import profileActions from '../../common/profileActions';
import licenseActions from '../../common/licenseActions';
import DeleteLicenseModal from './DeleteLicenseModal';
import EditOrCreateLicenseInformation from './EditOrCreateLicenseInformation';
import './EditLicenseInformation.scss';
import ReviewLicensingPanel from './ReviewLicensingPanel';
import LicenseVerificationStatus from './LicenseVerificationStatusPill';

export type Props = {
  serviceProviderId: number,
  profile: Object,
  getThimbleUrl: typeof profileActions.getThimbleUrl,
  getAtlasOrgStates: typeof licenseActions.getAtlasOrgStates,
  deleteTradeLicenseInfo: typeof profileActions.deleteTradeLicenseInfo,
  updateTradeLicenseAttestation: typeof profileActions.updateTradeLicenseAttestation,
  saveBondedInsured: typeof profileActions.saveBondedInsured,
  getServiceProviderCurrentProfile: typeof profileActions.getServiceProviderProfile,
  sendEnhancedTrackEvent: typeof analyticsActions.sendEnhancedTrackEvent,
}

const insuranceIcon = require('../assets/insurance.svg');

const licenseSignature = {
  'Not Licensed': 'I am not appropriately licensed for my trade',
  'Not Applicable': 'Not applicable, my trade does not require licensing',
  'In Progress': 'My license application is in progress',
  Licensed: 'Yes, I am appropriately licensed for my trade.',
};

const styles = {
  noLicensesText: {
    color: stylesColors.grey400,
  },
  licenseDetailsHeader: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
};

function editLicenseValues(editLicenseId, profile) {
  const currentLicenseInformation = get(profile, 'currentProfileUAPI.response.businessInfo.licenses', []).find(license => license.licenseId === editLicenseId);
  const maybeExpiration = get(currentLicenseInformation, 'expirationDate');
  const currentExpiration = maybeExpiration ? moment(maybeExpiration).format('YYYY-MM-DD') : undefined;

  const additionalFieldsObj = {};
  const trade = get(currentLicenseInformation, 'trade', '');

  if (trade.length > 0) {
    additionalFieldsObj.LicenseType = trade;
  }

  const verificationStatuses = get(currentLicenseInformation, 'verificationStatuses', []);
  const verificationPreviouslyRequested = verificationStatuses.find(status => status !== 'Unverified');

  return {
    licenseSignature: get(currentLicenseInformation, 'trade'),
    firstName: get(currentLicenseInformation, 'firstName'),
    lastName: get(currentLicenseInformation, 'lastName'),
    companyName: get(currentLicenseInformation, 'company'),
    licenseNumber: get(currentLicenseInformation, 'licenseNumber'),
    licensingAuthorityName: get(currentLicenseInformation, 'licenseAuthority'),
    licenseName: get(currentLicenseInformation, 'licenseName'),
    licensingAuthorityExternalId: get(currentLicenseInformation, 'licensingAuthorityExternalId'),
    licenseExpiration: currentExpiration,
    licenseLocale: get(currentLicenseInformation, 'state'),
    categories: get(currentLicenseInformation, 'categories', []),
    additionalFields: additionalFieldsObj,
    verify: !isUndefined(verificationPreviouslyRequested),
  };
}
function defaultLicenseValues(profile) {
  return {
    licenseSignature: 'Licensed',
    firstName: get(profile, 'currentProfileUAPI.response.userInfo.firstName'),
    lastName: get(profile, 'currentProfileUAPI.response.userInfo.lastName'),
    companyName: get(profile, 'currentProfileUAPI.response.businessInfo.businessName'),
    licenseStateAbbreviation: get(profile, 'currentProfileUAPI.response.contactInfo.address.state'),
  };
}

function licensingNeedsAttestation(licenses) {
  return licenses.some((license) => {
    const attestationDate = get(license, 'attestationDate');
    const attestationExpiration = moment(attestationDate).add(2, 'years');
    const attestationWindow = moment().add(30, 'days');
    return attestationExpiration.isSameOrBefore(attestationWindow);
  });
}

class EditLicenseInformationComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showThimbleModal: false,
      licenseToDelete: undefined,
      licenseToShowLoading: undefined,
      inEdit: false,
      editLicenseId: undefined,
      licensingAttested: false,
      autoHideReviewPanel: false,
      bondedInsured: {},
    };
  }

  componentDidMount() {
    const {
      getAtlasOrgStates, getThimbleUrl, serviceProviderId, profile,
    } = this.props;
    const primaryPostalCode = get(profile, 'currentProfileUAPI.response.contactInfo.address.postalCode');
    if (primaryPostalCode) {
      getThimbleUrl(serviceProviderId, get(profile, 'currentProfileUAPI.response.primaryAddress.postalCode'));
    }

    getAtlasOrgStates(serviceProviderId);
  }

  props: Props


  handleDeleteClick = tradeLicenseInfoId =>
    this.setState({
      licenseToDelete: tradeLicenseInfoId,
      licenseToShowLoading: tradeLicenseInfoId,
    });

  handleDeleteClose = () => this.setState({ licenseToDelete: undefined })

  handleDeleteConfirm = (uuid, licenseId) => {
    const { serviceProviderId, deleteTradeLicenseInfo } = this.props;

    deleteTradeLicenseInfo(serviceProviderId, uuid, licenseId);
    this.handleDeleteClose();
  }

  handleLicenseCardClick = (tradeLicenseInfoId) => {
    this.setState({ inEdit: true, editLicenseId: tradeLicenseInfoId });
  }

  handleEditCompletion = () => {
    const { getServiceProviderCurrentProfile, profile } = this.props;
    const uuid = get(profile, 'currentProfileUAPI.response.uuid', '');
    this.setState({ inEdit: false, editLicenseId: undefined });
    getServiceProviderCurrentProfile({ uuid });
  }

  handleAttestationConfirm = () => {
    const { updateTradeLicenseAttestation, serviceProviderId, sendEnhancedTrackEvent } = this.props;
    sendEnhancedTrackEvent({
      name: 'Item Clicked',
      properties: {
        ...defaultPageProps,
        activityLocation: 'SP : My Business : Licensing and insurance',
        description: 'Confirm Attestation Date',
      },
    });
    updateTradeLicenseAttestation(serviceProviderId);
    this.setState({ licensingAttested: true });
    window.setTimeout(() => {
      this.setState({ autoHideReviewPanel: true });
    }, 5000);
  }

  handleBondedInsuredChecked = (event) => {
    const { name, checked } = event.target;
    this.setState((prevState) => {
      const bondedInsured = { ...prevState.bondedInsured };
      bondedInsured[name] = checked;
      return { bondedInsured };
    });
  }

  handleBondedInsuredSubmit = () => {
    const { saveBondedInsured, serviceProviderId, profile } = this.props;
    const defaultBonded = get(profile, 'currentProfileUAPI.response.businessInfo.bonded', false);
    const defaultInsured = get(profile, 'currentProfileUAPI.response.businessInfo.insured', false);

    const uuid = get(profile, 'currentProfileUAPI.response.uuid', '');

    const bonded = isUndefined(this.state.bondedInsured.bonded)
      ? defaultBonded
      : this.state.bondedInsured.bonded;
    const insured = isUndefined(this.state.bondedInsured.insured)
      ? defaultInsured
      : this.state.bondedInsured.insured;
    saveBondedInsured(serviceProviderId, uuid, bonded, insured);
  }

  render() {
    const {
      profile, serviceProviderId,
    } = this.props;
    const thimbleUrl = get(profile, 'thimbleUrl.response.purchaseLink');
    const redirectToThimble = () => {
      this.setState({ showThimbleModal: true });
      const hideModal = () => this.setState({ showThimbleModal: false });
      window.setTimeout(() => {
        hideModal();
        window.open(thimbleUrl);
      }, 2000);
    };

    const licenses = get(profile, 'currentProfileUAPI.response.businessInfo.licenses', []);
    const licenseIdBeingDeleted = get(profile, 'deleteLicense.isLoading', false) ?
      this.state.licenseToShowLoading : undefined;
    const saveInProgress = get(profile, 'saveBondedInsured.isLoading', false);
    const bonded = get(profile, 'currentProfileUAPI.response.businessInfo.bonded', false);
    const insured = get(profile, 'currentProfileUAPI.response.businessInfo.insured', false);

    const uuid = get(profile, 'currentProfileUAPI.response.uuid', '');


    const editInitialValues = this.state.editLicenseId ?
      editLicenseValues(this.state.editLicenseId, profile) : defaultLicenseValues(profile);

    const displayReviewLicensingPanel = licensingNeedsAttestation(licenses)
      && !this.state.autoHideReviewPanel;

    return (
      <div id="License-Information--Container">
        <Choose>
          <When condition={this.state.inEdit}>
            <EditOrCreateLicenseInformation
              maybeEditLicenseInfoId={this.state.editLicenseId}
              serviceProviderId={serviceProviderId}
              editCompletionFunction={this.handleEditCompletion}
              initialValues={editInitialValues}
              uuid={uuid}
            />
          </When>
          <Otherwise>
            <div
              className="page-name"
              style={{ 'margin-left': '-10px' }}
            >
              Licensing
            </div>
            <p className="margin-l10 margin-b20" >
              Increase trust by letting customers know about your licenses and certifications.
              Share what makes you qualified for their home project.
            </p>
            <DeleteLicenseModal
              showModal={!isUndefined(this.state.licenseToDelete)}
              onHide={this.handleDeleteClose}
              onDelete={() => this.handleDeleteConfirm(uuid, this.state.licenseToDelete)}
              licenseNumber={this.state.licenseToDelete}
            />
            <If condition={!!displayReviewLicensingPanel}>
              <ReviewLicensingPanel
                licensingAttested={this.state.licensingAttested}
                onAttest={this.handleAttestationConfirm}
              />
            </If>
            <Row id="Edit-License-Information--Add-License-Row">
              <Col sm={7} md={8} lg={9}>
                <Choose>
                  <When condition={licenses.length > 0}>
                    <div id="Edit-License-Information--Add-License-Prompt">Click on a license to edit.</div>
                  </When>
                  <Otherwise>
                    <div id="Edit-License-Information--No-Licenses-Prompt" style={styles.noLicensesText}>You don&apos;t have any licenses.</div>
                  </Otherwise>
                </Choose>
              </Col>
              <Col sm={5} md={4} lg={3}>
                <Track>
                  <Button
                    id="Edit-License-Information--Add-License-Button"
                    bsSize="small"
                    bsStyle="default-office"
                    block
                    onClick={() => { this.setState({ inEdit: true, editLicenseId: undefined }); }}
                  >
                    Add New License
                  </Button>
                </Track>
              </Col>
            </Row>
            {licenses.map((license) => {
              const categoryNames = get(license, 'categories', []).map(cat => cat.name);
              return (
                <Track>
                  <Row
                    id={`Edit-LicenseInformation--Edit-License-${license.licenseId}`}
                    onClick={() => this.handleLicenseCardClick(license.licenseId)}
                    key={license.licenseId}
                  >
                    <Col xs={12}>
                      <Panel className="Edit-License-Information--License-Panel">
                        <Panel.Body>
                          <Row>
                            <Col xs={12} lg={6} lgPush={6} className="License-Verification-Status">
                              <Choose>
                                <When condition={license.licenseStatus}>
                                  <span className="License-Verification-Status-Pill">
                                    <LicenseVerificationStatus
                                      licenseVerificationStatus={license.licenseStatus}
                                    />
                                  </span>
                                </When>
                              </Choose>
                            </Col>
                            <Col xs={12} lg={6} lgPull={6}>
                              <div style={styles.licenseDetailsHeader}>Category</div>
                              <p>
                                <Choose>
                                  <When condition={categoryNames.length > 0}>
                                    { join(categoryNames, ', ') }
                                  </When>
                                  <Otherwise>
                                  Click to add a category
                                  </Otherwise>
                                </Choose>
                              </p>
                            </Col>
                          </Row>
                          <Row className="Edit-License-Information--License-Status-Row">
                            <Col xs={12} lg={6}>
                              <div style={styles.licenseDetailsHeader}>License Status</div>
                              <div className="Edit-License-Information--License-Details">
                                {licenseSignature[license.trade]}
                              </div>
                            </Col>
                            <If condition={license.licenseName}>
                              <Col xs={12} lg={6}>
                                <div style={styles.licenseDetailsHeader}>License Name</div>
                                <div className="Edit-License-Information--License-Details">
                                  {license.licenseName}
                                </div>
                              </Col>
                            </If>
                          </Row>
                          <If condition={license.trade === 'Licensed'}>
                            <Row className="Edit-License-Information--License-Details-Row">
                              <If condition={license.firstName || license.LastName}>
                                <Col xs={12} lg={3}>
                                  <div style={styles.licenseDetailsHeader}>Name</div>
                                  <div className="Edit-License-Information--License-Details">
                                    <span style={{ whiteSpace: 'nowrap' }}>
                                      {`${get(license, 'firstName', '')} ${get(license, 'lastName', '')}`}
                                    </span>
                                  </div>
                                </Col>
                              </If>
                              <Col xs={12} lg={3}>
                                <div style={styles.licenseDetailsHeader}>Company</div>
                                <div className="Edit-License-Information--License-Details">
                                  {license.company}
                                </div>
                              </Col>
                              <Col xs={12} lg={3}>
                                <div style={styles.licenseDetailsHeader}>Expiration</div>
                                <div className="Edit-License-Information--License-Details">{license.expirationDate}</div>
                              </Col>
                              <Col xs={12} lg={3}>
                                <div style={styles.licenseDetailsHeader}>License Number</div>
                                <div className="Edit-License-Information--License-Details">{license.licenseNumber}</div>
                              </Col>
                            </Row>
                          </If>
                          <div className="text-right">
                            <Choose>
                              <When
                                condition={
                                  licenseIdBeingDeleted === license.licenseId
                                }
                              >
                                <i className="icon icon-load icon-spin" />
                              </When>
                              <Otherwise>
                                <Track>
                                  <Button
                                    id={`Edit-License-Information--Delete-License-${license.licenseId}`}
                                    className="text-secondary"
                                    bsStyle="link"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      this.handleDeleteClick(license.licenseId);
                                    }}
                                  >
                                    <i className="icon icon-trash-can" />
                                  </Button>
                                </Track>
                              </Otherwise>
                            </Choose>
                          </div>
                        </Panel.Body>
                      </Panel>
                    </Col>
                  </Row>
                </Track>);
              })}
            <div className="Insurance-and-bonds">Insurance and bonds</div>
            <Row styles={{ textAlign: 'right' }}>
              <Col xs={12} lg={6}>
                <div>Check all that apply to your business.</div>
                <Track>
                  <Input
                    name="bonded"
                    label="Bonded"
                    id="Edit-License-Information--License-Bonded"
                    type="checkbox"
                    defaultChecked={bonded}
                    onClick={(event) => {
                      this.handleBondedInsuredChecked(event);
                    }}
                  />
                </Track>
                <Track>
                  <Input
                    name="insured"
                    label="Insured"
                    id="Edit-License-Information--License-Insured"
                    type="checkbox"
                    defaultChecked={insured}
                    onClick={(event) => {
                      this.handleBondedInsuredChecked(event);
                    }}
                  />
                </Track>
              </Col>
            </Row>
            <Row>
              <Col style={{ textAlign: 'right' }} smPush={7} sm={5} mdPush={8} md={4} lgPush={9} lg={3}>
                <Button
                  id="Edit-License-Information--Bonded-Insured-Save-Button"
                  flat
                  bsStyle="primary-office"
                  type="submit"
                  loading={saveInProgress}
                  disabled={saveInProgress}
                  onClick={this.handleBondedInsuredSubmit}
                >
                  Save
                </Button>
              </Col>
            </Row>
            <hr />
            <Row style={{ display: 'flex', justifyContent: 'center' }}>
              <Col>
                <If condition={thimbleUrl}>
                  <If condition={this.state.showThimbleModal}>
                    <ThimbleTransitionBanner />
                  </If>
                  <Well
                    id="Edit-License-Information--Thimble-Container"
                  >
                    <div className="pull-left text-center" id="Edit-License-Information--Insurance-Badge-Container">
                      <img src={insuranceIcon} alt="Need liability insurance?" style={{ height: 40, width: 36 }} />
                    </div>
                    <div style={{ marginRight: 5 }}>
                      <strong>Need liability insurance?</strong>
                      <br />
                      Get Business Insurance - starting at $30/month
                      <br />
                      <Track>
                        <Button
                          bsStyle="link"
                          bsSize="small"
                          onClick={redirectToThimble}
                          id="Edit-License-Information--Thimble-Link"
                          style={{ whiteSpace: 'nowrap', marginLeft: 0 }}
                        >
                          Purchase from Thimble in 60 seconds. <i className="icon icon-right-open" />
                        </Button>
                      </Track>
                    </div>
                  </Well>
                </If>
              </Col>
            </Row>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

const editLicenseAnalytics = {
  page: {
    name: 'SP : My Business : Licensing and insurance',
    properties: {
      ...defaultPageProps,
    },
  },
  elements: {
    'Edit-License-Information--License-Bonded': itemClicked('SP : My Business : Licensing and insurance', 'My Business : Bonded'),
    'Edit-License-Information--License-Insured': itemClicked('SP : My Business : Licensing and insurance', 'My Business : Insured'),
    'Edit-License-Information--Thimble-Link': itemClicked('SP : My Business : Licensing and insurance', 'My Business : Thimble Learn More Clicked'),
    'Edit-License-Information--Add-License-Button': itemClicked('SP : My Business : Licensing and insurance', 'Add New License'),
    'Edit-License-Information--Delete-License-*': itemClicked('SP : My Business : Licensing and insurance', 'Remove License'),
    'Edit-LicenseInformation--Edit-License-*': itemClicked('SP : My Business : Licensing and insurance', 'Edit License Details'),
    'Delete-License-Modal--Cancel-Button': itemClicked('SP : My Business : LBI : Remove Single License Modal', 'Cancel'),
    'Delete-License-Modal--Delete-Button': itemClicked(
      'SP : My Business : LBI : Remove Single License Modal',
      'Removed Selected License',
      { licenseNumber: '<<attributes.data-track-license-number.value>>' },
    ),
  },
};

export default compose(
  connect(state => ({
    profile: state.serviceProviderProfile,
    serviceProviderId: state.serviceProviderId,
  }), {
    ...profileActions,
    ...analyticsActions,
    ...licenseActions,
  }),
  analytics({
    ruleset: editLicenseAnalytics,
  }),
  pure,
)(EditLicenseInformationComponent);



// WEBPACK FOOTER //
// ./src/modules/profile/browser/components/EditLicenseInformation.js